/**
 * Identitas asisten yang sama untuk chat (prompt Claude) dan suara (instruksi
 * sesi GPT-Live). Nama selalu dari `AssistantSettings.assistantName`; modul
 * ini murni (tanpa DB) sehingga dipakai server, halaman uji, dan test.
 */

export const VILLAGE_NAME = "Desa Darmasaba";
export const ASSISTANT_NAME_MAX_CHARS = 60;
const FALLBACK_NAME = "Asisten";

/** Batas instruksi sesi GPT-Live (dokumen OpenAI `/live/sessions`). */
export const LIVE_INSTRUCTIONS_MAX = 500;

/** Nama aman untuk prompt: satu baris, tanpa karakter kontrol, maks 60 karakter. */
export function cleanAssistantName(name: string): string {
	return (
		name
			.replace(/[\p{Cc}\p{Cf}]+/gu, " ")
			.replace(/\s+/g, " ")
			.trim()
			.slice(0, ASSISTANT_NAME_MAX_CHARS) || FALLBACK_NAME
	);
}

/**
 * Aturan identitas lapisan Identitas prompt chat (keputusan #55): larangan
 * selalu disertai pengganti, tanpa menyebut nama vendor agar kata itu tidak tertanam.
 */
export function identityDisclosureRule(name: string): string {
	const clean = cleanAssistantName(name);
	return `- Bila ditanya model, vendor, atau teknologi yang dipakai: jawab bahwa kamu ${clean}, asisten virtual Dashboard ${VILLAGE_NAME}, dan detail teknis bisa ditanyakan ke admin. Jangan menyebut nama model atau perusahaan teknologi, dan jangan menyangkal bahwa kamu asisten AI.`;
}

/** Nama di instruksi suara dipotong agar persona terpanjang tetap menyisakan ruang di batas 500. */
export const VOICE_NAME_MAX_CHARS = 16;

/** Kalimat tunggu bawaan; harus sama dengan `DEFAULT_WAIT_PHRASES` (tipe style) dan default kolom DB. */
const FALLBACK_WAIT_PHRASES: readonly string[] = [
	"Tunggu sebentar ya",
	"Saya carikan dulu ya",
];

/** Kalimat tunggu bersih: satu baris, tanpa tanda kutip & karakter kontrol, maks 40 karakter; kosong dibuang. */
export function cleanWaitPhrases(phrases: readonly string[]): string[] {
	const clean = phrases
		.map((p) =>
			p
				.replace(/[\p{Cc}\p{Cf}"“”/]+/gu, " ")
				.replace(/\s+/g, " ")
				.trim()
				.slice(0, WAIT_PHRASE_MAX_CHARS)
				.trim(),
		)
		.filter(Boolean)
		.slice(0, WAIT_PHRASES_MAX);
	return clean.length > 0 ? clean : [...FALLBACK_WAIT_PHRASES];
}

/** Batas daftar kalimat tunggu — sama dengan `STYLE_LIMITS` (config tidak mengimpor tipe style). */
export const WAIT_PHRASES_MAX = 5;
export const WAIT_PHRASE_MAX_CHARS = 40;

/** Persona GPT-Live final (keputusan #55): perintah positif, delegasi semua pertanyaan, larangan + pengganti. */
export function buildVoicePersona(name: string): string {
	const n = cleanAssistantName(name).slice(0, VOICE_NAME_MAX_CHARS).trim();
	return `Kamu ${n}, asisten Dashboard ${VILLAGE_NAME}. Teruskan SETIAP pertanyaan ke backend, bacakan jawabannya apa adanya. Ditanya teknologi: kamu ${n}, detail tanya admin.`;
}

/**
 * Aturan kalimat tunggu (bug V1-B): GPT-Live mengarang "Saya tanya dulu" yang terdengar
 * bertanya ke orang lain. Perintah positif + daftar tertutup; peran "kamu yang mencari"
 * menggantikan kata larangan agar kata itu tidak tertanam.
 */
export function buildWaitRule(phrases: readonly string[]): string {
	const list = cleanWaitPhrases(phrases).join(" / ");
	return `Kamu yang mencari data. Menunggu: ucapkan satu saja lalu diam: ${list}.`;
}

const WORST_PHRASES = Array.from({ length: WAIT_PHRASES_MAX }, () =>
	"x".repeat(WAIT_PHRASE_MAX_CHARS),
);

/** Panjang persona + aturan tunggu terpanjang — sisa kuota 500 untuk tambahan bacakan-persis dari admin. */
export const VOICE_FIXED_MAX_CHARS =
	buildVoicePersona("x".repeat(VOICE_NAME_MAX_CHARS)).length +
	1 +
	buildWaitRule(WORST_PHRASES).length;

/** Batas setelan admin `voiceReadExactInstruction` (+1 untuk pemisah baris). */
export const VOICE_READ_EXACT_MAX_CHARS =
	LIVE_INSTRUCTIONS_MAX - VOICE_FIXED_MAX_CHARS - 1;

/** Bawaan tambahan bacakan-persis bila admin belum mengisi; persona sudah memuat "apa adanya". */
export const VOICE_READ_EXACT_DEFAULT = "Jangan meringkas atau berkomentar.";

/** Instruksi sesi GPT-Live lengkap: persona + aturan tunggu + bacakan-persis (dipotong agar ≤ 500 karakter). */
export function buildLiveSessionInstruction(
	name: string,
	readExact: string | null | undefined,
	waitPhrases: readonly string[] = FALLBACK_WAIT_PHRASES,
): string {
	const fixed = `${buildVoicePersona(name)}\n${buildWaitRule(waitPhrases)}`;
	const extra = (readExact ?? "").trim() || VOICE_READ_EXACT_DEFAULT;
	return `${fixed}\n${extra}`.slice(0, LIVE_INSTRUCTIONS_MAX);
}
